<template>
  <v-container :class="{ 'pa-0': $vuetify.breakpoint.xsOnly }">
    <Toolbar :title="$vuetify.breakpoint.xsOnly ? $t('message.your_orders') : null" />
    <GenericError v-if="error" />
    <v-card v-else outlined>
      <!-- Big Devices -->
      <template v-if="$vuetify.breakpoint.smAndUp">
        <v-toolbar elevation="0" color="white">
          <v-toolbar-title>{{ $t('message.your_orders') }}</v-toolbar-title>
        </v-toolbar>
        <v-data-table
          :headers="headers"
          :items="items"
          :options.sync="options"
          :loading="isLoading"
          :server-items-length="totalItems"
          :footer-props="{ 'items-per-page-options': [5, 10, 25, 100] }"
        >
          <template v-slot:body="{ items }">
            <tbody>
              <tr
                v-for="item in items"
                :key="item['@id']"
                style="cursor: pointer;"
                @click="$router.push({ name: 'order', params: { id: item.id } })"
              >
                <td v-if="item.grossTotal > 0" :class="'text-no-wrap status status-' + item.status">
                  <v-icon>{{ statusToIcon(item) }}</v-icon>
                  <span class="text-uppercase ml-2">{{ $t('message.' + item.status) }}</span>
                </td>
                <td v-else class="text-no-wrap status status-updated">
                  <v-icon>{{ statusToIcon(item) }}</v-icon>
                  <span class="text-uppercase ml-2">updated balance</span>
                </td>

                <td class="text-no-wrap">{{ item.issuedAt | moment('ll') }}</td>
                <td class="text-no-wrap">
                  <router-link
                    :to="{
                      name: 'AgreementShow',
                      params: {
                        id: $iriToId(item.connectAgreement['@id'])
                      }
                    }"
                    @click.native.stop
                  >
                    {{ item.connectAgreement.site.name }}
                  </router-link>
                </td>
                <td class="text-no-wrap">
                  <div v-if="item.lines.length > 0">
                    <span v-for="line in item.lines" :key="line.id">
                      <template v-if="line.type">
                        {{ $t('message.' + line.type) }}
                      </template>
                      <span v-else class="grey--text">{{ $t('message.no_line_type') }}</span>
                    </span>
                  </div>
                </td>
                <td class="text-no-wrap">
                  <v-icon small color="grey">{{
                    typeToIcon(getRelevantPayment(item) ? getRelevantPayment(item).type : 'unpaid')
                  }}</v-icon>
                  {{ getRelevantPayment(item) ? $t('message.' + getRelevantPayment(item).type) : $t('message.unpaid') }}
                </td>
                <td class="text-right text-no-wrap">
                  <template v-if="item.grossTotal > 0">
                    {{ $n(item.grossTotal, item.currency.code) }}
                  </template>
                </td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </template>
      <!-- Small Devices -->
      <template v-else>
        <v-list two-line class="pa-0">
          <template v-for="(item, index) in listItems">
            <v-list-item
              :key="item.id"
              :to="{ name: 'order', params: { id: item.id } }"
              :class="'status status-' + item.status"
            >
              <v-list-item-content>
                <v-list-item-title>
                  <template v-if="getPeriod(item)">
                    {{
                      $t('message.order_list_title', {
                        startsAt: $moment(getPeriod(item).startsAt).format('YYYY-MM-DD'),
                        endsAt: $moment(getPeriod(item).endsAt).format('YYYY-MM-DD'),
                        airtimePlan: getPeriod(item).airtimePlan.name,
                        servicePlan: item.connectAgreement.servicePlan.name
                      })
                    }}
                  </template>
                  <template v-else>{{ item.connectAgreement.site.name }}</template>
                </v-list-item-title>
                <v-list-item-subtitle>
                  <v-icon small color="grey">{{
                    typeToIcon(getRelevantPayment(item) ? getRelevantPayment(item).type : 'unpaid')
                  }}</v-icon>
                  {{ getRelevantPayment(item) ? $t('message.' + getRelevantPayment(item).type) : $t('message.unpaid') }}
                  <span class="text-lowercase">{{
                    isPaymentPending(item) ? '(' + $t('message.' + item.status) + ')' : null
                  }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-list-item-action-text class="text-right">
                  <v-list-item-title>{{ item.issuedAt | moment('from', 'now') }}</v-list-item-title>
                  <v-list-item-subtitle class="subheading font-weight-medium">{{
                    $n(item.grossTotal, item.currency.code)
                  }}</v-list-item-subtitle>
                </v-list-item-action-text>
              </v-list-item-action>
            </v-list-item>
            <v-divider v-if="index + 1 < listItems.length" :key="index"></v-divider>
          </template>
        </v-list>
        <v-row v-if="isLoading" align="center" justify="center" class="py-4 fill-height">
          <v-progress-circular indeterminate size="25" color="primary"></v-progress-circular>
        </v-row>
      </template>
    </v-card>
  </v-container>
</template>

<script>
import GenericError from '@/views/error/GenericError'
import { mapActions, mapGetters } from 'vuex'
import Toolbar from '@/components/Toolbar'

export default {
  name: 'OrderList',
  components: {
    GenericError,
    Toolbar
  },

  data: () => ({
    snackbar: false,
    localDeletedItem: null,
    localUpdatedItem: null,
    localCreatedItem: null,
    deleteConfirmShow: false,
    selectedForDelete: null,
    listItems: [],
    headers: [],
    options: {
      page: 1,
      sortBy: ['issuedAt'],
      sortDesc: [true],
      itemsPerPage: 25
    }
  }),
  computed: {
    ...mapGetters({
      deletedItem: 'order/del/deleted',
      error: 'order/list/error',
      items: 'order/list/items',
      isLoading: 'order/list/isLoading',
      view: 'order/list/view',
      totalItems: 'order/list/totalItems',
      createdItem: 'order/create/created',
      updatedItem: 'order/update/updated',
      deleteError: 'order/del/error',
      deleteLoading: 'order/del/isLoading'
    })
  },

  watch: {
    items: function(items) {
      if (!items) {
        return
      }

      this.pushItemsToListItems(items)
    },

    deletedItem: function(deleted) {
      if (deleted) {
        this.localDeletedItem = this.deletedItem
        this.snackbar = true
        this.getPage()
        this.reset()
      }
    },

    options: {
      handler: function(options, optionsOld) {
        if (!options) {
          return
        }
        if (
          options.itemsPerPage !== optionsOld.itemsPerPage ||
          options.page !== optionsOld.page ||
          options.sortBy !== optionsOld.sortBy ||
          options.sortDesc !== optionsOld.sortDesc
        ) {
          this.getPaginatedPage(options)
        }
      },
      deep: true
    }
  },

  mounted() {
    this.scroll()

    this.getPaginatedPage(this.options)

    if (this.deletedItem || this.updatedItem || this.createdItem) {
      this.localDeletedItem = this.deletedItem
      this.localUpdatedItem = this.updatedItem
      this.localCreatedItem = this.createdItem

      this.snackbar = true
    }
    this.reset()

    this.headers = [
      { text: '', value: 'status' },
      { text: this.$t('message.issued_at'), value: 'issuedAt' },
      {
        text: this.$t('message.site'),
        value: 'connectAgreement.site.name'
      },
      { text: this.$t('message.line_type'), value: 'line_type', sortable: false },
      { text: this.$t('message.payment_type'), value: 'type', sortable: false },
      {
        text: this.$t('message.total'),
        value: 'grossTotal',
        sortable: false,
        align: 'right'
      }
    ]
  },

  beforeDestroy() {
    window.onscroll = () => {}
  },

  methods: {
    selectForDelete(item) {
      this.selectedForDelete = item
      this.deleteConfirmShow = true
    },

    onDeleteConfirm() {
      this.deleteConfirmShow = false
      this.deleteItem(this.selectedForDelete)
    },

    onDeleteCancel() {
      this.deleteConfirmShow = false
      this.selectedForDelete = null
    },

    reset() {
      this.updateReset()
      this.delReset()
      this.createReset()
    },

    getPaginatedPage(options) {
      let query = '/orders' + '?itemsPerPage=' + options.itemsPerPage + '&page=' + options.page

      if (options.sortBy.length > 0) {
        query = query + '&order[' + options.sortBy[0] + ']=' + (options.sortDesc[0] ? 'desc' : 'asc')
      }

      query += '&exists[connectAgreement]=true'

      this.getPage(query)
    },

    scroll() {
      window.onscroll = () => {
        let bottomOfWindow =
          Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop) +
            window.innerHeight ===
          document.documentElement.offsetHeight

        if (
          !this.isLoading &&
          bottomOfWindow &&
          this.listItems.length < this.totalItems &&
          this.$vuetify.breakpoint.xsOnly
        ) {
          this.options.page = this.options.page + 1
          this.getPaginatedPage(this.options)
        }
      }
    },

    pushItemsToListItems(items) {
      items.forEach(item => {
        if (this.listItems.indexOf(item) === -1) {
          this.listItems.push(item)
        }
      })
    },

    isPaymentPending(order) {
      let donePyments = order.payments.filter(payment => {
        return 'done' === payment.status
      })

      return donePyments.length === 0
    },

    getRelevantPayment(order) {
      let donePyments = order.payments.filter(payment => {
        return 'done' === payment.status
      })

      if (donePyments.length > 0) {
        return donePyments[0]
      }

      let pendingPyments = order.payments.filter(payment => {
        return 'pending' === payment.status
      })

      if (pendingPyments.length > 0) {
        return pendingPyments[0]
      }

      if (order.payments.length > 0) {
        return order.payments[0]
      }

      return null
    },

    getPeriod(order) {
      let period = null
      order.lines.forEach(function(line) {
        if (line.servicePeriod) {
          period = line.servicePeriod
        }
      })

      return period
    },

    humanizePeriod(period) {
      let startsAt = this.$moment(period.startsAt)
      let endsAt = this.$moment(period.endsAt)
      let duration = this.$moment.duration(startsAt.diff(endsAt)).humanize()
      return duration.charAt(0).toUpperCase() + duration.slice(1)
    },

    typeToIcon(type) {
      switch (type) {
        case 'credit_card_off_session':
        case 'credit_card_online':
          return 'mdi-credit-card'
        case 'credit_card_offline':
          return 'mdi-point-of-sale'
        case 'direct_debit':
          return 'mdi-receipt'
        case 'transfer':
          return 'mdi-bank'
        case 'airtime_balance':
          return 'mdi-wallet'
        case 'unknown':
          return 'mdi-help-circle-outline'
        default:
          return 'mdi-cancel'
      }
    },

    statusToIcon(order) {
      if ('cancelled' === order.status) {
        return 'mdi-cancel'
      }

      if ('expired' === order.status) {
        return 'mdi-timer-off'
      }

      if ('done' === order.status) {
        return 'mdi-thumb-up'
      }

      return 'mdi-account-clock'
    },

    ...mapActions({
      getPage: 'order/list/default',
      deleteItem: 'order/del/del',
      createReset: 'order/create/reset',
      delReset: 'order/del/reset',
      updateReset: 'order/update/reset'
    })
  }
}
</script>

<style lang="stylus" scoped>
.status {
  border-left: 8px solid;
}

.status-done {
  border-color: #4CAF50;
  color: #4CAF50;

  .theme--light.v-icon {
    color: #4CAF50;
  }
}

.status-pending {
  border-color: #FF9800;
  color: #FF9800;

  .theme--light.v-icon {
    color: #FF9800;
  }
}

.status-failed {
  border-color: #F44336;
  color: #F44336;

  .theme--light.v-icon {
    color: #F44336;
  }
}

.status-cancelled,
.status-expired {
  border-color: #9E9E9E;
  color: #9E9E9E;

  .theme--light.v-icon {
    color: #9E9E9E;
  }
}

.status-updated{
  border-color: #3b79f5;
  color: #3b79f5;

  .theme--light.v-icon {
  color: #3b79f5;
  }
}
</style>
